"use client"
import * as React from "react"
import { useTranslations } from "@/i18n"
import { coreTextLimits } from "@ibada/shared-types/common"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm, useWatch } from "react-hook-form"
import { useApplyApiFieldErrors, useApiFormBannerMessage } from "@/shared/lib/api-errors/api-form-errors"
import { getApiFormErrorDetails } from "@/shared/lib/api-errors/form-api-errors"
import {
  createOfferingTypeFormSchema,
  defaultOfferingTypeFormValues,
  type OfferingTypeFormInput,
} from "@/features/church/config/offering-types/schemas/offering-type-form.schema"
import { OfferingTypeScopeFields } from "@/features/church/config/offering-types/components/OfferingTypeScopeFields"
import {
  OfferingTypeScope,
  OfferingTypeStatus,
  type OfferingType,
} from "@/features/church/config/offering-types/types/offering-type.types"
import { Button } from "@ibada/ui"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@ibada/ui"
import {
  Form,
  FormControlledField,
  FormSelectField,
} from "@ibada/ui"
import { Input } from "@ibada/ui"
import { Textarea } from "@ibada/ui"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { MobileDetailScreen } from "@/shared/components/mobile-detail-screen"
import {
  MobileFormError,
  MobileFormInputRow,
  MobileFormSection,
  MobileFormSelectRow,
  MobileFormTextArea,
} from "@/shared/components/mobile-form"
type OfferingTypeFormModalProps = {
  open: boolean
  mode: "create" | "edit"
  initialOfferingType?: OfferingType | null
  branches: Array<{ id: string; name: string }>
  canManageParishScope: boolean
  hideBranchField?: boolean
  fixedScope?: OfferingTypeScope
  fixedBranchId?: string | null
  isSubmitting: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (values: OfferingTypeFormInput) => Promise<void>
}

const OFFERING_TYPE_STATUS_OPTIONS = [
  OfferingTypeStatus.ACTIVE,
  OfferingTypeStatus.DISABLED,
] as const

const OFFERING_TYPE_FIELD_NAME_MAP: Partial<Record<string, keyof OfferingTypeFormInput | null>> = {
  branch: "ownerBranchId",
  branchId: "ownerBranchId",
  ownerBranchId: "ownerBranchId",
}

export function OfferingTypeFormModal({
  open,
  mode,
  initialOfferingType,
  branches,
  canManageParishScope,
  hideBranchField = false,
  fixedScope,
  fixedBranchId = null,
  isSubmitting,
  onOpenChange,
  onSubmit,
}: OfferingTypeFormModalProps) {
  const t = useTranslations("churchConfig.offeringTypes.form")
  const applyFieldErrors = useApplyApiFieldErrors<OfferingTypeFormInput>("churchConfig.offeringTypes")
  const formBannerMessage = useApiFormBannerMessage("churchConfig.offeringTypes")
  const isMobile = useIsMobile()
  const tMobile = useTranslations("common.mobileForm")
  const schema = React.useMemo(
    () =>
      createOfferingTypeFormSchema({
        nameMin: t("errors.nameMin"),
        nameMax: t("errors.nameMax"),
        descriptionMax: t("errors.descriptionMax"),
        scopeRequired: t("errors.scopeRequired"),
        ownerBranchRequired: t("errors.ownerBranchRequired"),
      }),
    [t]
  )
  const form = useForm<OfferingTypeFormInput>({
    resolver: zodResolver(schema),
    defaultValues: defaultOfferingTypeFormValues,
  })
  const scope = useWatch({ control: form.control, name: "scope" })
  React.useEffect(() => {
    if (!open) return

    if (mode === "edit" && initialOfferingType) {
      form.reset({
        name: initialOfferingType.name,
        description: initialOfferingType.description ?? "",
        scope: fixedScope ?? initialOfferingType.scope,
        ownerBranchId:
          fixedBranchId ??
          (fixedScope === OfferingTypeScope.PARISH ? "" : (initialOfferingType.ownerBranchId ?? "")),
        status: initialOfferingType.status,
      })
      return
    }
    form.reset({
      ...defaultOfferingTypeFormValues,
      scope: fixedScope ?? (canManageParishScope ? OfferingTypeScope.PARISH : OfferingTypeScope.BRANCH),
      ownerBranchId:
        fixedScope === OfferingTypeScope.PARISH
          ? ""
          : (fixedBranchId ?? defaultOfferingTypeFormValues.ownerBranchId),
    })
  }, [canManageParishScope, fixedBranchId, fixedScope, form, initialOfferingType, mode, open])

  React.useEffect(() => {
    if (fixedScope) {
      form.setValue("scope", fixedScope)
      return
    }
    if (canManageParishScope) return
    form.setValue("scope", OfferingTypeScope.BRANCH)
  }, [canManageParishScope, fixedScope, form])

  React.useEffect(() => {
    if (scope !== OfferingTypeScope.PARISH) return
    form.setValue("ownerBranchId", "")
  }, [form, scope])

  React.useEffect(() => {
    if (scope !== OfferingTypeScope.BRANCH) return
    if (fixedBranchId) {
      form.setValue("ownerBranchId", fixedBranchId)
      return
    }
    const current = form.getValues("ownerBranchId")
    if (current) return
    const firstBranchId = branches[0]?.id
    if (!firstBranchId) return
    form.setValue("ownerBranchId", firstBranchId)
  }, [branches, fixedBranchId, form, scope])

  const handleSubmit = form.handleSubmit(async (values) => {
    form.clearErrors()

    try {
      await onSubmit(values)
    } catch (error) {
      applyFieldErrors(form.setError, error, { fieldNameMap: OFFERING_TYPE_FIELD_NAME_MAP })

      if (Object.keys(getApiFormErrorDetails(error).fieldErrors).length > 0) {
        return
      }

      form.setError("root.serverError", { type: "server", message: formBannerMessage(error) })
    }
  })

  const statusOptions = OFFERING_TYPE_STATUS_OPTIONS.map((value) => ({
    value,
    label: value === OfferingTypeStatus.ACTIVE ? t("status.active") : t("status.disabled"),
  }))

  if (isMobile) {
    // Phone: a full screen (Annuler / title / Créer) of grouped rows; the branch and status open pickers.
    const values = form.watch()
    const errors = form.formState.errors
    const picker = { doneLabel: tMobile("done"), emptyLabel: tMobile("noResults") }
    const set = <TName extends keyof OfferingTypeFormInput>(name: TName, value: OfferingTypeFormInput[TName]) =>
      form.setValue(name, value as never, { shouldDirty: true, shouldValidate: form.formState.isSubmitted || Boolean(errors[name]) })
    const showBranch = scope === OfferingTypeScope.BRANCH && !hideBranchField && !fixedBranchId
    return (
      <MobileDetailScreen
        open={open}
        onOpenChange={onOpenChange}
        title={mode === "create" ? t("createTitle") : t("editTitle")}
        backLabel={t("cancel")}
        showBackChevron={false}
        action={{
          label: mode === "create" ? t("createSubmit") : t("editSubmit"),
          disabled: isSubmitting,
          onPress: () => void handleSubmit(),
        }}
      >
        <div>
          <MobileFormSection>
            <MobileFormInputRow
              label={t("fields.name")}
              value={values.name}
              onChange={(event) => set("name", event.target.value)}
              placeholder={t("fields.namePlaceholder")}
              maxLength={coreTextLimits.offeringTypeName}
              invalid={Boolean(errors.name)}
            />
            {showBranch ? (
              <MobileFormSelectRow
                {...picker}
                label={t("fields.branch")}
                placeholder={t("fields.branchPlaceholder")}
                value={values.ownerBranchId || undefined}
                onChange={(value) => set("ownerBranchId", value ?? "")}
                options={branches.map((branch) => ({ value: branch.id, label: branch.name }))}
                invalid={Boolean(errors.ownerBranchId)}
              />
            ) : null}
            <MobileFormSelectRow
              {...picker}
              label={t("fields.status")}
              placeholder={tMobile("choose")}
              value={values.status}
              onChange={(value) => value && set("status", value as OfferingTypeStatus)}
              options={statusOptions}
            />
          </MobileFormSection>
          <MobileFormError message={errors.name?.message ?? errors.ownerBranchId?.message} />
        </div>
        <div>
          <MobileFormSection title={t("fields.description")}>
            <MobileFormTextArea
              value={values.description ?? ""}
              onChange={(event) => set("description", event.target.value)}
              placeholder={t("fields.descriptionPlaceholder")}
              maxLength={coreTextLimits.offeringTypeDescription}
              aria-label={t("fields.description")}
            />
          </MobileFormSection>
          <MobileFormError message={errors.description?.message ?? errors.root?.serverError?.message} />
        </div>
      </MobileDetailScreen>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent fullHeight size='2xl'>
        <DialogHeader>
          <DialogTitle>{mode === "create" ? t("createTitle") : t("editTitle")}</DialogTitle>
          <DialogDescription>
            {mode === "create" ? t("createDescription") : t("editDescription")}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form className='space-y-4' onSubmit={handleSubmit}>
            <FormControlledField
              control={form.control}
              name='name'
              label={t("fields.name")}
              renderInput={({ field }) => (
                <Input {...field} maxLength={coreTextLimits.offeringTypeName} showCount placeholder={t("fields.namePlaceholder")} />
              )}
            />

            <FormControlledField
              control={form.control}
              name='description'
              label={t("fields.description")}
              renderInput={({ field }) => (
                <Textarea
                  {...field}
                  value={field.value ?? ""}
                  maxLength={coreTextLimits.offeringTypeDescription}
                  showCount
                  placeholder={t("fields.descriptionPlaceholder")}
                  rows={3}
                />
              )}
            />

            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              <OfferingTypeScopeFields
                control={form.control}
                scope={scope}
                branches={branches}
                hideBranchField={hideBranchField || Boolean(fixedBranchId)}
                t={t}
              />

              <FormSelectField
                control={form.control}
                name='status'
                label={t("fields.status")}
                options={statusOptions}
              />
            </div>
            {form.formState.errors.root?.serverError?.message ? (
              <p className='text-destructive text-sm'>
                {form.formState.errors.root.serverError.message}
              </p>
            ) : null}
            <DialogFooter>
              <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
                {t("cancel")}
              </Button>
              <Button type='submit' loading={isSubmitting}>
                {mode === "create" ? t("createSubmit") : t("editSubmit")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
